/*******************************************************************************
 * Copyright (c) 2023 GetChkd
 *
 * All rights reserved.
 *
 * Contributors:
 *  James Worthington - Initial implementation
 *  Amina Rais - Massive Improvements
 *  Version: 2.0.0
 *
 *******************************************************************************/
import React, { useEffect, useCallback } from 'react';
import {
    Grid,
    CardContent,
    Typography,
    Button,
    Avatar,
    List,
    ListItem
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { convertFormatPhone } from '../../../../../../utils/convertFormatPhone.utils';
import { getUserInfo } from '../../../../../../store/reducers/user/userAction';
import { IMAGES } from '../../../../../../config/assets';

const UserProfileInformations = ({ toggleEditPwd, toggleEdit, changeLoading }) => {
    const { classes } = useStyles();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const user = useSelector(state => state.user.user);

    // Fetch user information from Redux store
    const fetchUserInfo = useCallback(async () => {
        try {
            await dispatch(getUserInfo()).unwrap();
        } catch (error) {
            console.error("Error fetching user info:", error);
        } finally {
            changeLoading(false);
        }
    }, [dispatch, changeLoading]);

    // Fetch user info when the component mounts
    useEffect(() => {
        fetchUserInfo();
    }, [fetchUserInfo]);

    return (
        <CardContent>
            <Grid container spacing={3}>
                {/* User Avatar */}
                <Grid item xs={12} sm={12} md={6}>
                    <Grid container spacing={4}>
                        <Grid item xs={12} style={{ marginTop: '60px' }}>
                            <Grid container spacing={4} justifyContent="center" alignItems="center">
                                <Grid item xs={3} style={{ display: 'flex', justifyContent: 'center' }}>
                                    <Avatar 
                                        src={user.useravatar || IMAGES.AVATAR_DEFAULT} 
                                        className={classes.avatar} 
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>

                {/* User Information */}
                <Grid item xs={12} sm={12} md={6}>
                    <List component="nav" disablePadding>
                        {[
                            { label: "User Name", value: user.username },
                            { label: "Last Name", value: user.lastname },
                            { label: "First Name", value: user.firstname },
                            { label: "Email", value: user.email },
                            { 
                                label: "Cellphone", 
                                value: user.cellphone ? convertFormatPhone.getFormattedPhoneNum(user.cellphone) : 'N/A' 
                            }
                        ].map(({ label, value }, index) => (
                            <ListItem key={index} disableGutters>
                                <Typography variant="h4">
                                    <b>{label}:</b> {value || 'N/A'}
                                </Typography>
                            </ListItem>
                        ))}
                    </List>
                </Grid>

                {/* Action Buttons */}
                <Grid item xs={12} sm={12} md={6}>
                    <Button
                        variant="contained"
                        color="primary"
                        style={{ marginTop: '20px', marginBottom: '20px' }}
                        onClick={() => { toggleEditPwd(false); toggleEdit(true); }}
                    >
                        Edit Contact Information
                    </Button>
                    <Button
                        variant="outlined"
                        color="primary"
                        style={{ marginTop: '20px', marginBottom: '20px', marginLeft: '20px' }}
                        onClick={() => { toggleEditPwd(true); toggleEdit(false); }}
                    >
                        Edit Password
                    </Button>
                    <Button
                        style={{ marginTop: '20px', marginLeft: '20px', marginBottom: '20px' }}
                        onClick={() => navigate('/dashboard')}
                    >
                        Return
                    </Button>
                </Grid>
            </Grid>
        </CardContent>
    );
};

// Styles using Material-UI's styling solution
const useStyles = makeStyles({ name: 'UserProfileInformations' })(theme => ({
    avatar: {
        margin: 'auto',
        width: '150px !important',
        height: '150px !important',
    },
}));

export default UserProfileInformations;
